import React, { useCallback, useContext, useState } from 'react'
import { useDispatch, useSelector } from 'hooks/use-redux'
import { bindActionCreators } from 'redux'
import type { PlaylistId } from 'types/playlist'
import type { Track } from 'types/track'
import {
  getTrackStatusDetails,
  isAggregatedSearchTrack,
} from '@disco/utils/tracks'
import {
  loadPane,
  updatePane,
} from '@disco/state-management/actions/ExplorerActions'
import { addNestedTracksToMain } from '@disco/state-management/actions/NestingActions'
import {
  resetSelection,
  selectExplorerItems,
} from '@disco/state-management/actions/SelectionActions'
import { uploadFilesToMainTrack } from '@disco/state-management/actions/upload-actions'
import { SelectionContext } from 'contexts/selection'
import {
  getAllSelectedItems,
  getExplorerSelection,
} from '@disco/state-management/selectors/selection'
import { getTrackTextSettings } from '@disco/state-management/selectors/user'
import { handleModifierKeysSelection } from '@disco/utils/selection'
import type { ExplorerTrackProps } from 'components/explorer/track'
import ExplorerTrack from 'components/explorer/track'
import TrackDropWrapper from 'containers/TrackDropWrapper'
type Props = ExplorerTrackProps & {
  disableClickHandling?: boolean
  disabledDrop?: boolean
  disablePaneOpen?: boolean
  index: number
  itemPrefix?: string
  sourcePlaylistId: PlaylistId | null | undefined
  track: Track
}

const ExplorerTrackItem = ({
  appearance,
  disableClickHandling,
  disabledDrop,
  disablePaneOpen,
  disableSelection,
  index,
  isSimilarity,
  itemPrefix = 'pane',
  pane,
  sourcePlaylistId,
  track,
  versionsButton,
  sourceGroupId,
  sourceGroupType,
  searchDragData,
}: Props) => {
  const dispatch = useDispatch()
  const selectionContext = useContext(SelectionContext)
  const [showTrackVersions, setShowTrackVersions] = useState<boolean>(false)
  const selectedItems = useSelector(getAllSelectedItems)
  const explorerSelection = useSelector(getExplorerSelection)
  const textSettings = useSelector(getTrackTextSettings)

  // Exposes playback affordances to the DOM so E2E tests can target the first
  // row that actually starts audio playback, rather than blindly using the
  // first row (which may be a non-audio file or an out-of-territory track).
  const { fileType, isPlayable } = getTrackStatusDetails(track)
  const isPlayableInTerritory = isPlayable && !track.outside_your_territories
  const trackItemTestAttributes = {
    'data-testid': `explorer-track-item-${track.id}`,
    'data-file-type': fileType,
    'data-playable': isPlayableInTerritory,
  }

  const handleTrackClick = useCallback(
    (event: MouseEvent) => {
      // handle selection actions
      if (
        (event.metaKey || event.ctrlKey || event.shiftKey) &&
        !disableSelection
      ) {
        handleModifierKeysSelection(event, {
          selection: explorerSelection,
          index,
          selectionId: selectionContext.id,
          pane,
          selectItems: (_pane, selectionRange, selectionType) =>
            dispatch(
              selectExplorerItems(
                selectionContext.id,
                selectionRange,
                selectionType,
              ),
            ),
        })
        return
      }

      // handle regular clicks, regular click reset selection
      dispatch(resetSelection())

      if (disablePaneOpen) {
        return
      }

      dispatch(updatePane({ ...pane, activeItem: `${itemPrefix}/${index}` }))
      dispatch(
        loadPane({
          index: pane.index + 1,
          title: track.name,
          type: 'track',
          url: track.url,
          // Properties for event #1 from https://disco-team.atlassian.net/browse/DISCO-5985
          additionalMixpanelProps: {
            'Track ID': track.id,
            Catalog: track.origin?.source_library?.id,
            'Catalog Business': track.origin?.business?.id,
            ...(isAggregatedSearchTrack(track)
              ? {
                  Context: 'Aggregated search',
                }
              : {}),
          },
        }),
      )
    },
    [
      dispatch,
      explorerSelection,
      index,
      pane,
      disablePaneOpen,
      disableSelection,
      itemPrefix,
      selectionContext.id,
      track,
    ],
  )

  const renderTrack = () => (
    <ExplorerTrack
      appearance={appearance}
      index={index}
      isSimilarity={isSimilarity}
      pane={pane}
      showTrackVersions={showTrackVersions}
      sourcePlaylistId={sourcePlaylistId}
      track={track}
      versionsButton={versionsButton}
      onShowTrackVersions={setShowTrackVersions}
      onClick={disableClickHandling ? undefined : handleTrackClick}
      sourceGroupId={sourceGroupId}
      sourceGroupType={sourceGroupType}
      searchDragData={searchDragData}
      textSettings={textSettings}
    />
  )

  if (disabledDrop) {
    return <div {...trackItemTestAttributes}>{renderTrack()}</div>
  }

  return (
    <div {...trackItemTestAttributes}>
      <TrackDropWrapper
        index={index}
        itemPrefix={itemPrefix}
        pane={pane}
        selectedItems={selectedItems}
        track={track} // TODO: enable nesting
        // https://disco-team.atlassian.net/browse/DISCO-5771
        disabled={track?.read_only}
        onDragHold={() => setShowTrackVersions(true)}
        {...bindActionCreators(
          {
            uploadFilesToMainTrack,
            addNestedTracksToMain,
          },
          dispatch,
        )}
      >
        {renderTrack()}
      </TrackDropWrapper>
    </div>
  )
}

export default React.memo<Props>(ExplorerTrackItem)
